import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// Add-on "Promociones" (fidelización con sellos).
//
// API (`/loyalty/*`):
//   programs            GET / POST · PUT /{id} · DELETE /{id}
//   customers?search=   GET  → [{ id, name, phone, email }]
//   customers           POST { name, phone, email } (alta rápida)
//   customers/{id}/balances  GET → [balance]
//   customers/{id}/ledger    GET → [movimiento]
//   customers/{id}/adjust    POST { programId, qty, reason }
//   customers/{id}/stamp     POST { programId, qty, idSession?, note? } ("+1 consumición")
//   recent-payments?search=  GET → cobros de hoy
//   recent-payments/{sessionId}/customer  POST { customerId } (olvidó la tarjeta)
//   session/{idSession}           GET → estado de fidelización del ticket
//   session/{idSession}/customer  PUT { customerId | null }
//   redeem              POST { idSession, idMesa, programId, pedidoId, counterProductId }
//   redeem-points       POST { idSession, idMesa, programId, rewardId, pedidoId?, productoId?, counterProductId? }
//   unredeem/{id}       POST
//   report?from&to      GET
//
// El cliente de fidelización es independiente del cliente de FACTURA
// (`payment.idCustomer`, que convierte el ticket en factura F1).

const initialState = {
  programs: [],
  programsLoaded: false,
  isLoading: false,
  // Estado de fidelización del ticket abierto en el TPV:
  //   { sessionId, customer, balances[], suggestions[], redemptions[], pendingStamps[] }
  session: null,
  // Cliente elegido para un cobro rápido SIN sesión persistida.
  pendingCustomer: null,
};

const slice = createSlice({
  name: "loyalty",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.isLoading = !!action.payload;
    },
    setPrograms(state, action) {
      state.programs = Array.isArray(action.payload) ? action.payload : [];
      state.programsLoaded = true;
    },
    upsertProgram(state, action) {
      const p = action.payload;
      if (!p) return;
      const i = state.programs.findIndex((x) => x.id === p.id);
      if (i >= 0) state.programs[i] = p;
      else state.programs.push(p);
    },
    removeProgram(state, action) {
      state.programs = state.programs.filter((x) => x.id !== action.payload);
    },
    setSession(state, action) {
      state.session = action.payload || null;
    },
    setPendingCustomer(state, action) {
      state.pendingCustomer = action.payload || null;
    },
    clearLoyaltyTicket(state) {
      state.session = null;
      state.pendingCustomer = null;
    },
  },
});

export default slice.reducer;
export const { setPendingCustomer, clearLoyaltyTicket } = slice.actions;

// ── Promociones ──────────────────────────────────────────────────────────
export function fetchLoyaltyPrograms() {
  return async (dispatch) => {
    dispatch(slice.actions.setLoading(true));
    try {
      const res = await axios.get("/loyalty/programs");
      dispatch(slice.actions.setPrograms(res.data));
      return res.data;
    } finally {
      dispatch(slice.actions.setLoading(false));
    }
  };
}

export function saveLoyaltyProgram(program) {
  return async (dispatch) => {
    const res = program.id
      ? await axios.put(`/loyalty/programs/${program.id}`, program)
      : await axios.post("/loyalty/programs", program);
    dispatch(slice.actions.upsertProgram(res.data));
    return res.data;
  };
}

/** «Eliminar» archiva: sale de la lista y queda en «Eliminadas» (reactivable). */
export function deleteLoyaltyProgram(id) {
  return async (dispatch) => {
    await axios.delete(`/loyalty/programs/${id}`);
    dispatch(slice.actions.removeProgram(id));
  };
}

/** Promociones eliminadas (pestaña «Eliminadas»). No toca la lista principal. */
export async function fetchDeletedLoyaltyPrograms() {
  const res = await axios.get("/loyalty/programs", { params: { deleted: true } });
  return Array.isArray(res.data) ? res.data : [];
}

/** Reactiva una eliminada: vuelve activa a la lista; saldos de clientes intactos. */
export function restoreLoyaltyProgram(id) {
  return async (dispatch) => {
    const res = await axios.post(`/loyalty/programs/${id}/restore`, {});
    dispatch(slice.actions.upsertProgram(res.data));
    return res.data;
  };
}

/** Borrado definitivo (solo eliminadas que nunca se usaron; si no, 409). */
export async function purgeLoyaltyProgram(id) {
  await axios.delete(`/loyalty/programs/${id}/permanent`);
}

// ── Clientes ─────────────────────────────────────────────────────────────
export async function searchLoyaltyCustomers(search = "") {
  const res = await axios.get("/loyalty/customers", { params: { search } });
  return Array.isArray(res.data) ? res.data : [];
}

export async function createLoyaltyCustomer(dto) {
  const res = await axios.post("/loyalty/customers", dto);
  return res.data;
}

export async function fetchCustomerBalances(customerId) {
  const res = await axios.get(`/loyalty/customers/${customerId}/balances`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function fetchCustomerLedger(customerId) {
  const res = await axios.get(`/loyalty/customers/${customerId}/ledger`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function adjustCustomerStamps(customerId, { programId, qty, reason }) {
  const res = await axios.post(`/loyalty/customers/${customerId}/adjust`, {
    programId,
    qty: Number(qty),
    reason,
  });
  return res.data;
}

export async function fetchLoyaltyReport(from, to) {
  const res = await axios.get("/loyalty/report", { params: { from, to } });
  return res.data;
}

// ── Ticket del TPV ───────────────────────────────────────────────────────
export function fetchLoyaltySession(idSession) {
  return async (dispatch) => {
    if (!idSession) {
      dispatch(slice.actions.setSession(null));
      return null;
    }
    const res = await axios.get(`/loyalty/session/${idSession}`);
    const data = res.data ? { ...res.data, sessionId: idSession } : null;
    dispatch(slice.actions.setSession(data));
    return data;
  };
}

// Vincula (o desvincula con null) el cliente de fidelización al ticket.
// Sin sesión persistida (cobro rápido) se guarda en local y viaja en el
// cobro como `loyaltyCustomerId`.
export function setLoyaltyCustomer(idSession, customer) {
  return async (dispatch) => {
    if (!idSession) {
      dispatch(slice.actions.setPendingCustomer(customer || null));
      if (customer?.id) {
        const balances = await fetchCustomerBalances(customer.id);
        dispatch(
          slice.actions.setSession({
            sessionId: null,
            customer,
            balances,
            suggestions: [],
            redemptions: [],
            pendingStamps: [],
          }),
        );
      } else {
        dispatch(slice.actions.setSession(null));
      }
      return;
    }
    const res = await axios.put(`/loyalty/session/${idSession}/customer`, {
      customerId: customer?.id ?? null,
    });
    dispatch(slice.actions.setPendingCustomer(customer || null));
    dispatch(
      slice.actions.setSession(res.data ? { ...res.data, sessionId: idSession } : null),
    );
    return res.data;
  };
}

// "+1 consumición": suma sellos a mano al cliente (LOYALTY_STAMP).
export function addManualStamps(customerId, { programId, qty = 1, idSession = null, note = "" }) {
  return async (dispatch, getState) => {
    const res = await axios.post(`/loyalty/customers/${customerId}/stamp`, {
      programId,
      qty: Number(qty),
      idSession,
      note: note || null,
    });
    const data = res.data || {};
    if (data.session) {
      dispatch(slice.actions.setSession({ ...data.session, sessionId: idSession }));
    } else {
      const current = getState().loyalty?.session;
      if (current?.customer?.id === customerId) {
        dispatch(slice.actions.setSession({ ...current, balances: data.balances || current.balances }));
      }
    }
    return data;
  };
}

// Cobros de hoy (para el cliente que olvidó la tarjeta).
export async function fetchRecentPayments(search = "") {
  const res = await axios.get("/loyalty/recent-payments", { params: { search } });
  return Array.isArray(res.data) ? res.data : [];
}

export async function linkRecentPayment(sessionId, customerId) {
  const res = await axios.post(`/loyalty/recent-payments/${sessionId}/customer`, { customerId });
  return res.data;
}

export function redeemLoyaltyReward({ idSession, idMesa, programId, pedidoId, productoId, counterProductId }) {
  return async (dispatch) => {
    const res = await axios.post("/loyalty/redeem", {
      idSession,
      idMesa,
      programId,
      pedidoId,
      productoId,
      counterProductId,
    });
    dispatch(
      slice.actions.setSession(res.data ? { ...res.data, sessionId: idSession } : null),
    );
    return res.data;
  };
}

// Canje de una recompensa de puntos (descuento en euros o producto gratis).
// Se aplica sobre líneas ya comandadas sin cobrar.
export function redeemLoyaltyPoints({ idSession, idMesa, programId, rewardId, pedidoId, productoId, counterProductId }) {
  return async (dispatch) => {
    const res = await axios.post("/loyalty/redeem-points", {
      idSession,
      idMesa,
      programId,
      rewardId,
      pedidoId: pedidoId ?? null,
      productoId: productoId ?? null,
      counterProductId: counterProductId ?? null,
    });
    dispatch(
      slice.actions.setSession(res.data ? { ...res.data, sessionId: idSession } : null),
    );
    return res.data;
  };
}

export function unredeemLoyaltyReward(redemptionId, idSession) {
  return async (dispatch) => {
    const res = await axios.post(`/loyalty/unredeem/${redemptionId}`);
    dispatch(
      slice.actions.setSession(res.data ? { ...res.data, sessionId: idSession } : null),
    );
    return res.data;
  };
}

// Selector: id del cliente de fidelización del ticket actual.
export const selectLoyaltyCustomerId = (state) =>
  state.loyalty?.session?.customer?.id ?? state.loyalty?.pendingCustomer?.id ?? null;
